import { Container } from "pixi.js";
import type { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { ReelAnticipationFrame } from "@slot/mechanics-reels";
import type { PresentationTaskRunner } from "@slot/presentation";

/**
 * Game-side anticipation-frame view: `AnticipationFrame.prefab`. The reel mechanic drives it via playIn()/playOut();
 * the animation is GAME-OWNED: an alpha fade in/out on the CHROME runner (constant perceived speed). The Cocos prefab
 * carried the base view (an instant show / hide) and left the title's fade animator unattached; the fade is what the
 * title wrote (plan D7).
 *
 * Tree (from the prefab dump): root 235.1×1004.4 > the frame's Spine at (2.6, -11.7 y-up) scaled 0.512, looping its
 * `loop` clip (`in` / `out` are never played).
 */
export class AnticipationFrameView implements ReelAnticipationFrame {
  public readonly node: Container;
  public fadeInSeconds = 0.2;
  public fadeOutSeconds = 0.2;
  private readonly runner: PresentationTaskRunner;
  private generation = 0;

  public constructor(input: { spine: Spine | null; chromeRunner: PresentationTaskRunner }) {
    this.runner = input.chromeRunner;
    this.node = new Container({ label: "AnticipationFrame" });
    this.node.alpha = 0;
    this.node.visible = false;
    if (input.spine) {
      input.spine.position.set(2.6, 11.7);
      input.spine.scale.set(0.512);
      if (input.spine.skeleton.data.findAnimation("loop")) input.spine.state.setAnimation(0, "loop", true);
      this.node.addChild(input.spine);
    }
  }

  public playIn(): void {
    this.node.visible = true;
    void this.fadeTo(1, this.fadeInSeconds);
  }

  public playOut(): void {
    void this.fadeTo(0, this.fadeOutSeconds).then((completed) => {
      if (completed) this.node.visible = false;
    });
  }

  private async fadeTo(target: number, seconds: number): Promise<boolean> {
    const gen = ++this.generation;
    const node = this.node;
    const from = node.alpha;
    const scaled = Math.max(0, seconds);
    if (scaled <= 0) {
      node.alpha = target;
      return true;
    }
    await this.runner.tweenProgress(scaled, (k) => {
      if (gen === this.generation && !node.destroyed) node.alpha = from + (target - from) * k;
    });
    return gen === this.generation;
  }
}
